import {MaybeReading, WithAPI} from "../api/temperature";
import {Card, InnerCard} from "./generic/card";
import {ChartContext, DashboardChart, SynchronizedCharts} from "./chart";
import {Temperature, TemperatureContext, useTemperature} from "./generic/temperature";
import {DateTime} from "./generic/date";
import {useFetchAndRender} from "./fetchAndRender";
import {CSSContext, ThemeContext} from "./types";
import {createRef} from "preact";
import {useContext, useEffect, useRef, useState} from "preact/hooks";
import {Column, Columns} from "./generic/columns";
import styled from "styled-components";
import {darken, rgba} from "polished";
import {Sensor, SensorsResponse} from "../api/sensor";
import {SensorSet} from "../api/set";
import * as echarts from "echarts/core";
import {EChartOption} from "echarts";

type DashboardCardProps = {
    sensor: Sensor
} & WithAPI;


type BoxProps = {
    title: string;
    reading: MaybeReading
};

const Box = styled(function ({title, reading, ...props}: BoxProps) {
    return <div {...props}>
        <h2>{title}</h2>
        <span class={"number" + ((!reading) ? " text-muted" : "")}><Temperature value={reading?.value ?? null} /></span>
        <small style={{whiteSpace: "nowrap"}}><DateTime value={reading?.date} short={true} /></small>
    </div>
})`
    display: flex;
    flex-direction: column;
    margin-bottom: 1rem;
    padding: 0.5rem;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    flex-grow: 1;
        
    > * {
        overflow: hidden;
        text-overflow: ellipsis;
    }
    
    > .number {
        display: block;
        font-size: 1.3rem;
        font-weight: bold;
        order: -1;
        line-height: 2rem;
    }
    
    > h2 {
        text-transform: uppercase;
        font-weight: normal;
        order: 1;
        margin: 0;
    }
    
    > small, > h2 {
        font-size: 0.75rem;
        line-height: 1.5em;
        color: ${props => props.theme.colors.mutedFg};
    }
`;

const StyledDashboardCard = styled(Card)`
    box-shadow: 0.1rem 0.1rem 0.3rem ${props => rgba(props.theme.colors.fg, 0.12)};
    
    ${InnerCard} {
        border: solid 1px ${props => darken(0.1, props.theme.colors.cardBg)};
        display: flex;
        flex-direction: column;
        overflow: visible;

        > header {
            color: ${props => props.theme.dashboard?.headerTextColor ?? props.theme.colors.cardFg};
            text-shadow: 0.05rem 0.05rem 0.2rem ${props => props.theme.colors.bg};
            background: ${props => props.theme.colors.cardBg} linear-gradient(to bottom, ${props => rgba(props.theme.dashboard?.headerTextColor ?? props.theme.colors.cardFg, .1)}, ${props => rgba(props.theme.colors.cardBg, 1)});
            padding: 0;

            h1 {
                font-size: 0.8rem;
                text-align: left;
                text-transform: uppercase;
                padding: 0.5rem;
                flex-grow: 1;

                > a {
                    text-decoration: none;

                    &:hover {
                        font-weight: inherit;
                    }
                }
            }
        }
    }
`;

const Readings = styled.section`
    display: flex;
    padding-left: .5rem;
    padding-right: .5rem;
`;

const ChartContainer = styled.div`
    height: 7rem;
    border-color: ${props => rgba(props.theme.colors.mutedFg, .2)};
    color: ${props => props.theme.dashboard?.chartTextColor ?? props.theme.colors.mutedFg};
    background-color: ${props => rgba(props.theme.colors.cardBg, .6)};
    font-size: .8rem;
`;

export function DashboardCard(props: DashboardCardProps) {
    const chartRef = createRef<HTMLDivElement>();
    const [chartCSSContext, setChartCSSContext] = useState<CSSContext>();
    const temp = useTemperature();

    useEffect(() => {
        if (chartRef.current && !chartCSSContext) {
            setChartCSSContext({
                style: window.getComputedStyle(chartRef.current),
                clientWidth: chartRef.current.clientWidth,
                clientHeight: chartRef.current.clientHeight
            });
        }
    }, [chartRef]);

    // Update chart with new value without the need to reload whole dataset.
    useEffect(() => {
        if (chartRef.current && props.sensor.last) {
            const chartDiv = chartRef.current.querySelector<HTMLDivElement>(".echarts-for-react");
            if (chartDiv) {
                const chart = echarts.getInstanceByDom(chartDiv);
                if (chart) {
                    const reading = [new Date(props.sensor.last.date), temp.convert(props.sensor.last.value), props.sensor.last.value];
                    const threshold = new Date(new Date().getTime() - 48 * 86400000);

                    const series: EChartOption.SeriesLine = ((chart.getOption() as EChartOption).series as EChartOption.Series[])[0] as EChartOption.SeriesLine;
                    const data = [
                        // Keep only last 48 hours
                        ...((series.data ?? []) as [Date, number, number][])
                            .filter(([dt, val]) => dt > threshold),

                        // And the new reading.
                        reading
                    ];

                    chart.setOption({
                        series: [{
                            data: data
                        }]
                    });
                }
            }
        }
    }, [props.sensor]);

    return <StyledDashboardCard title={<a href={props.sensor.url}>{props.sensor.name}</a>}>
        <Readings>
            <Box title={props.sensor.active ? "Current" : "Last"} reading={props.sensor.last}/>
            <Box title="Day max" reading={props.sensor.active ? props.sensor.dayMax : null}/>
            <Box title="Day min" reading={props.sensor.active ? props.sensor.dayMin : null}/>
        </Readings>
        <ChartContainer ref={chartRef}>
            <ThemeContext.Consumer>{() =>
                <CSSContext.Provider value={chartCSSContext ?? {}}>
                    <DashboardChart api={props.api} sensor={props.sensor} />
                </CSSContext.Provider>
            }</ThemeContext.Consumer>
        </ChartContainer>
    </StyledDashboardCard>;
}

const DashboardColumns = styled(Columns)`
    justify-content: center;
`;

const DashboardColumn = styled(Column)`
    flex-basis: 26rem;
    max-width: 26rem;
    width: 100%;
    margin-bottom: 1rem;
`;

type DashboardViewProps = {
    set: SensorSet;
} & WithAPI;

export function DashboardView(props: DashboardViewProps) {
    const [result] = useFetchAndRender<SensorsResponse>(() => props.api.sensor.get({
        filter: {
            sets: [props.set.id]
        },
        attributes: ["id", "name", "url", "last", "dayMax", "dayMin", "active"]
    }));

    const [chartContext, setChartContext] = useState<ChartContext>({
        min: null,
        max: null,
        setMin: (newMin: number) => {
            if (newMin !== chartContextRef.current.min) {
                const newCtx = Object.assign({}, chartContextRef.current);
                newCtx.min = newMin;
                chartContextRef.current.min = newMin;
                setChartContext(newCtx);
            }
        },
        setMax: (newMax: number) => {
            if (newMax !== chartContextRef.current.max) {
                const newCtx = Object.assign({}, chartContextRef.current);
                newCtx.max = newMax;
                chartContextRef.current.max = newMax;
                setChartContext(newCtx);
            }
        }
    });
    const chartContextRef = useRef(chartContext);

    const temperatureContext = useContext(TemperatureContext);
    useEffect(() => {
        setChartContext(Object.assign({}, chartContext, {min: null, max: null}));
    }, [temperatureContext])

    return <SynchronizedCharts.Provider value={chartContext}>
        <DashboardColumns>{result?.sensors.map(sensor =>
            <DashboardColumn key={sensor.id}>
                <DashboardCard sensor={sensor} api={props.api} />
            </DashboardColumn>
        )}</DashboardColumns>
    </SynchronizedCharts.Provider>;
}
